import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorBoundary, { Error } from 'src/components/errors';
import ViewWrapper from 'src/components/view-wrapper';
import { useActiveUserClasses } from 'src/hooks/use-active-user-classes';
import useNetworkStatus from 'src/hooks/use-network-status';
import { useUserGrade } from 'src/hooks/use-user-grade';
import AuthService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import realtimeService from 'src/services/realtime.service';
import { dayjs } from 'src/utils/dayjs-config.utils';

import { useCurrentLanguageTranslation } from './hooks/use-current-language-translation';

const App: FC = () => {
  const isOnline = useNetworkStatus();
  const { userGrade, isGradesLoading } = useUserGrade();

  const { activeUserClasses, isError: isActiveUserClassesFetchError } =
    useActiveUserClasses();

  const { i18n } = useTranslation();

  useCurrentLanguageTranslation();

  useEffect(() => {
    const changeDayjsLocale = (lng: string) => {
      dayjs.locale(lng);
    };

    changeDayjsLocale(i18n.language);

    i18n.on('languageChanged', changeDayjsLocale);

    return () => {
      i18n.off('languageChanged', changeDayjsLocale);
    };
  }, [i18n]);

  useEffect(() => {
    if (!userGrade || isGradesLoading) return;

    eventService.init(AuthService.getUserBk(), userGrade);
  }, [isGradesLoading, userGrade]);

  useEffect(() => {
    if (!activeUserClasses?.length) return;

    realtimeService.init(activeUserClasses);
  }, [activeUserClasses]);

  if (!isOnline) {
    return <Error withImage={false} />;
  }

  if (isActiveUserClassesFetchError) {
    return <Error />;
  }

  return (
    <ErrorBoundary>
      <ViewWrapper />
    </ErrorBoundary>
  );
};

export default App;
